/**
 * Utility functions for formatting data
 *
 * Date/time formatting lives in dateHelpers.js.
 * This file has phone number formatting only.
 */

/**
 * Formats a phone number string to a casual chat format
 * Uses xxx-xxx-xxxx (no country code for US, just dashes)
 * @param {string} phoneNumber - The phone number to format
 * @returns {string} - The formatted phone number
 */
export const formatPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return '';

  // Remove all non-numeric characters
  const cleaned = phoneNumber.replace(/\D/g, '');

  // Format based on length - casual style with just dashes
  if (cleaned.length === 10) {
    // Format as xxx-xxx-xxxx for US numbers
    return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  } else if (cleaned.length === 11 && cleaned[0] === '1') {
    // Strip country code, format as xxx-xxx-xxxx
    return `${cleaned.slice(1, 4)}-${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  }

  // Return original if we can't format it
  return phoneNumber;
};

/**
 * Normalizes a phone number for wa.me links
 * wa.me requires E.164 format: country code + number (no + sign)
 *
 * Rules:
 * - E.164 format (+19176502256): Strip the + and return
 * - 10 digits: Assume US, prepend "1"
 * - 11 digits starting with "1": Already has US country code
 * - Other lengths: Return as-is (assume user included country code)
 *
 * @param {string} phoneNumber - The phone number to normalize (E.164 or legacy format)
 * @returns {string} - Phone number ready for wa.me link
 */
export const formatPhoneForWhatsApp = (phoneNumber) => {
  if (!phoneNumber) return '';

  // Handle E.164 format (+19176502256) - just strip the +
  if (phoneNumber.startsWith('+')) {
    return phoneNumber.slice(1);
  }

  // Remove all non-numeric characters
  const cleaned = phoneNumber.replace(/\D/g, '');

  // 10 digits = US number without country code, prepend "1"
  if (cleaned.length === 10) {
    return '1' + cleaned;
  }

  // Return as-is for all other cases (11+ digits or international)
  return cleaned;
};
